"use client"

import Link from "next/link"
import { useEffect, useState } from "react"

import { armStickyBarSecondTapGuard } from "@/components/marketing/sticky-second-tap-guard"
import { CtaLabel } from "@/lib/articles/cta-measure"

/**
 * The mobile-only sticky bottom CTA: a slim pinned bar that recreates the
 * desktop masthead's always-available "Find your program" on the phones where
 * that button is gone (the masthead CTA survives to the `sm` breakpoint, so the
 * bar renders below 640px only).
 *
 * IT IS ON EVERY MARKETING PAGE OUTSIDE THE FUNNEL AND CHECKOUT CHROME. His
 * words: "make sure the find your program mobile bottom banner is ALLWAYS
 * present on all landing pages and sales pages and article pages", then "also
 * every single other page"; and later, on the application page: "Make sure the
 * Find Your Program Sticky Mobile banner does not pop up on the application
 * page." The marketing layout mounts it through the same ChromeSwitch as the
 * masthead and footer, so /apply, /book, /what-happens-next and the checkouts
 * render no bar, and every other route always does. It used to live on the
 * landing page alone, gated between two sentinel ids that only that page
 * provided, so it was absent from every other page and invisible for most of
 * the one page it was on.
 *
 * The sentinels survive as OPTIONAL, because the landing page still owns them
 * and a future page may want the same scroll behaviour. With none given, or
 * with ids that resolve to nothing, the bar simply stays up.
 */
export function MobileStickyCta({
  startAfterId,
  stopAtId,
}: {
  startAfterId?: string
  stopAtId?: string
}) {
  const gated = Boolean(startAfterId && stopAtId)
  const [pastStart, setPastStart] = useState(false)
  const [reachedStop, setReachedStop] = useState(false)

  useEffect(() => {
    if (!startAfterId || !stopAtId) return
    const start = document.getElementById(startAfterId)
    const stop = document.getElementById(stopAtId)
    // Ids that resolve to nothing mean this page has no sentinels, so the bar
    // stays up rather than waiting forever for an observer that never fires.
    if (!start || !stop) return
    const startObs = new IntersectionObserver(([entry]) => {
      setPastStart(!entry.isIntersecting && entry.boundingClientRect.top < 0)
    })
    const stopObs = new IntersectionObserver(([entry]) => {
      setReachedStop(entry.isIntersecting || entry.boundingClientRect.top < 0)
    })
    startObs.observe(start)
    stopObs.observe(stop)
    return () => {
      startObs.disconnect()
      stopObs.disconnect()
    }
  }, [startAfterId, stopAtId])

  const visible = gated ? pastStart && !reachedStop : true

  return (
    <div
      aria-hidden={!visible}
      className={`fixed inset-x-0 bottom-0 z-40 border-t border-[var(--color-rule)] bg-[var(--color-paper)] px-4 pt-2.5 transition-transform duration-200 ease-out sm:hidden ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
      style={{ paddingBottom: "calc(0.625rem + env(safe-area-inset-bottom))" }}
    >
      <Link
        href="/apply"
        data-attr={CtaLabel.StickyBar}
        onClick={(event) => armStickyBarSecondTapGuard(event.nativeEvent)}
        tabIndex={visible ? 0 : -1}
        className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[var(--color-vermilion)] text-[15px] font-bold text-white no-underline"
      >
        Find your program <span aria-hidden>&rsaquo;</span>
      </Link>
    </div>
  )
}
